import { Application, Calendar, Hook } from 'jamespot-front-business';
import { CalendarApi, CalendarView } from 'jamespot-react-components';
import { Fragment, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';

const Typography = commonComponentsRegistry.getLazyComponent('Typography');
const FlexBox = commonComponentsRegistry.getLazyComponent('FlexBox');
const ButtonDropdown = commonComponentsRegistry.getLazyComponent('ButtonDropdown');
const Dropdown = commonComponentsRegistry.getLazyComponent('DropDown');
const IconButton = commonComponentsRegistry.getLazyComponent('IconButton');
const InputSelectRaw = commonComponentsRegistry.getLazyComponent('InputSelectRaw');
const InputCheckboxRaw = commonComponentsRegistry.getLazyComponent('InputCheckboxRaw');
const StyledHref = commonComponentsRegistry.getLazyComponent('StyledHref');

const Label = styled(Typography)`
    text-transform: capitalize;
    margin-right: ${({ theme }) => theme.space.md}px;
`;

const InputWrapper = styled.div`
    max-width: 200px;
    display: flex;
    align-items: center;
`;

const DropdownContainer = styled.div`
    padding: 0 ${({ theme }) => theme.space.md}px;
`;

type Filter = ('socialEvent' | 'meeting' | 'animatedMeeting' | 'boardCard')[];
type OptionView = {
    label: string;
    value: CalendarView;
};

export const CalendarToolbar = ({
    label,
    view,
    viewsOptions,
    calendarApi,
    isLoading,
    navigateWithQuery,
    setView,
    setCreationType,
    setExportModalOpen,
}: {
    label?: string;
    view: OptionView;
    viewsOptions: { label: string; value: CalendarView }[];
    calendarApi?: CalendarApi;
    isLoading: boolean;
    navigateWithQuery: (queries: { types?: Filter; view?: CalendarView }) => void;
    setView: (view: any) => void;
    setCreationType: (type: 'socialEvent' | 'meeting' | 'animatedMeeting' | null) => void;
    setExportModalOpen: (value: boolean) => void;
}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { types } = useAppSelector(Calendar.selectors.selectCalendarEvents);
    const animatedMeeting = useAppSelector((state) => Application.selectors.selectById(state, 'AnimatedMeetingHook'));
    const animatedMeetingHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'AnimatedMeetingHook'));
    const meeting = useAppSelector((state) => Application.selectors.selectById(state, 'MeetingHook'));
    const boardCard = useAppSelector((state) => Application.selectors.selectById(state, 'BoardcardHook'));

    const createCalendarEventOptions = useMemo(
        () => [
            {
                label: intl.formatMessage({ id: 'CALENDAR_Create_SocialEvent' }),
                onClick: () => {
                    setCreationType('socialEvent');
                },
            },
            ...(meeting
                ? [
                      {
                          label: intl.formatMessage({ id: 'CALENDAR_Create_Meeting' }),
                          onClick: () => {
                              setCreationType('meeting');
                          },
                      },
                  ]
                : []),
            ...(animatedMeeting && animatedMeetingHook?.create
                ? [
                      {
                          label: intl.formatMessage({ id: 'CALENDAR_Create_AnimatedMeeting' }),
                          href: '/?action=spotlight-edit&type=animatedMeeting',
                      },
                  ]
                : []),
        ],
        [animatedMeeting, animatedMeetingHook?.create, intl, meeting, setCreationType],
    );
    const viewOptionEntries = useMemo(
        () => [
            {
                label: <FormattedMessage id="CALENDAR_SocialEvents" />,
                value: 'socialEvent',
            },
            ...(meeting
                ? [
                      {
                          label: <FormattedMessage id="CALENDAR_Meetings" />,
                          value: 'meeting',
                      },
                  ]
                : []),
            ...(animatedMeeting
                ? [
                      {
                          label: <FormattedMessage id="CALENDAR_AnimatedMeetings" />,
                          value: 'animatedMeeting',
                      },
                  ]
                : []),
            ...(boardCard
                ? [
                      {
                          label: <FormattedMessage id="CALENDAR_BoardCards" />,
                          value: 'boardCard',
                      },
                  ]
                : []),
        ],
        [animatedMeeting, boardCard, meeting],
    );

    return (
        <FlexBox y={'center'}>
            {view.value !== 'list' && (
                <Fragment>
                    <Label size={'l'} weight={'medium'}>
                        {label}
                    </Label>
                    <FlexBox>
                        <IconButton icon={'icon-chevron-left'} onClick={() => calendarApi?.prev()} color={'grey5'} />
                        <IconButton icon={'icon-chevron-right'} onClick={() => calendarApi?.next()} color={'grey5'} />
                    </FlexBox>
                    <IconButton
                        icon={'icon-calendar'}
                        onClick={() => calendarApi?.today()}
                        tooltip={{ description: 'CALENDAR_Today', position: 'bottom' }}
                        color={'grey5'}
                    />
                </Fragment>
            )}
            <InputWrapper>
                <InputSelectRaw<{ selectView: OptionView }>
                    onChange={setView}
                    value={view}
                    name={'selectView'}
                    options={viewsOptions}
                    getLabel={(option) => option.label}
                    getValue={(option) => option.value}
                    required
                    minWidth={false}
                    loading={isLoading}
                    noMargin
                />
            </InputWrapper>
            <ButtonDropdown
                noMargin
                color={'primary'}
                icon={'icon-plus'}
                label={intl.formatMessage({ id: 'GLOBAL_Add' })}
                options={createCalendarEventOptions}
                dataCy={'calendar-create-dropdown'}
            />
            <Dropdown
                trigger={
                    <IconButton
                        aria-label={intl.formatMessage({ id: 'GLOBAL_Filter' })}
                        colorVariant={'light'}
                        icon={'icon-filter'}
                        badgeNumber={types.length}
                    />
                }
                dropdown={() => (
                    <DropdownContainer>
                        <FlexBox direction={'column'}>
                            <Typography weight={'medium'} color={'primary'}>
                                {intl.formatMessage({ id: 'GLOBAL_Filter' })}
                            </Typography>
                            <InputCheckboxRaw<Filter>
                                labelPosition={'before'}
                                checkboxMode={'checkbox'}
                                direction={'column'}
                                onChange={(f: Filter) => {
                                    dispatch(Calendar.actions.setTypes(f));
                                    navigateWithQuery({ types: f });
                                }}
                                options={viewOptionEntries}
                                value={types}
                                name={'0'}
                            />
                            <StyledHref
                                onClick={() => {
                                    dispatch(Calendar.actions.setTypes([]));
                                    navigateWithQuery({ types: [] });
                                }}
                                as={'button'}
                                color={'cancel'}
                                disabled={types.length === 0}>
                                {intl.formatMessage({ id: 'GLOBAL_Reset' })}
                            </StyledHref>
                        </FlexBox>
                    </DropdownContainer>
                )}
                withBorder
                align={'right'}
            />
            <ButtonDropdown
                noMargin
                small
                smallOpenDirection={'left'}
                color={'primary'}
                variant={'outlined'}
                icon={'icon-ellipsis-v'}
                label={intl.formatMessage({ id: 'GLOBAL_Options' })}
                options={[
                    {
                        label: intl.formatMessage({ id: 'EVENT_Export_Calendar_ICalendar_Format' }),
                        onClick: () => setExportModalOpen(true),
                    },
                ]}
            />
        </FlexBox>
    );
};
